import { Component, Output, EventEmitter, HostListener, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-exercise-calendar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './exercise-calendar.html',
  styleUrls: ['./exercise-calendar.scss']
})
export class ExerciseCalendar implements OnInit, OnChanges {
  private _disabled: boolean = false;

  @Input()
  set disabled(value: boolean) {
    this._disabled = value;
  }

  get disabled(): boolean {
    return this._disabled;
  }

  // NOVO: Input para desabilitar meses pares
  @Input() disableEvenMonths: boolean = false;

  @Input() initialYear: number | null = null;
  @Input() initialMonth: number | null = null;

  // Input para exercícios disponíveis do backend
  @Input() availableExercicios: string[] = [];

  @Output() periodChange = new EventEmitter<{ year: number; month: number }>();

  isPeriodPickerOpen: boolean = false;
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number | null = new Date().getMonth() + 1;

  private originalYear: number = this.selectedYear;
  private originalMonth: number | null = this.selectedMonth;

  availableYears: number[] = [];
  availableMonths: string[] = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth() + 1;



  ngOnInit() {
    if (this.initialYear !== null) {
      this.selectedYear = this.initialYear;
      this.originalYear = this.initialYear;
    }

    if (this.initialMonth !== null) {
      this.selectedMonth = this.initialMonth;
      this.originalMonth = this.initialMonth;
    }
    this.initializeYears();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['availableExercicios'] && this.availableExercicios && this.availableExercicios.length > 0) {
      this.initializeYearsFromExercicios();

      // Se não há initialYear/initialMonth definidos, usa o primeiro exercício (mais recente)
      if (this.initialYear === null && this.initialMonth === null) {
        this.setPeriodFromFirstExercicio();
      }
    } else if (!this.availableExercicios || this.availableExercicios.length === 0) {
      this.initializeYears();
    }
  }

  private setPeriodFromFirstExercicio() {
    if (this.availableExercicios && this.availableExercicios.length > 0) {
      let selectedExercicio: string | null = null;

      // Se disableEvenMonths está ativo, procura o primeiro exercício com mês ímpar
      if (this.disableEvenMonths) {
        for (const exercicio of this.availableExercicios) {
          if (exercicio && exercicio.length === 6) {
            const month = parseInt(exercicio.substring(4, 6), 10);
            if (!isNaN(month) && month >= 1 && month <= 12 && month % 2 === 1) {
              selectedExercicio = exercicio;
              break; // Encontrou o primeiro mês ímpar
            }
          }
        }
      } else {
        // Se não está ativo, usa o primeiro exercício (mais recente)
        selectedExercicio = this.availableExercicios[0];
      }

      if (selectedExercicio && selectedExercicio.length === 6) {
        const year = parseInt(selectedExercicio.substring(0, 4), 10);
        const month = parseInt(selectedExercicio.substring(4, 6), 10);

        if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
          this.selectedYear = year;
          this.selectedMonth = month;
          this.originalYear = year;
          this.originalMonth = month;

          // Emite o evento para notificar o componente pai
          this.periodChange.emit({
            year: this.selectedYear,
            month: this.selectedMonth
          });
        }
      }
    }
  }

  initializeYears() {
    const currentYear = new Date().getFullYear();
    this.availableYears = [];
    for (let i = currentYear - 2; i <= currentYear + 2; i++) {
      this.availableYears.push(i);
    }
  }

  initializeYearsFromExercicios() {
    // Extrai anos únicos dos exercícios (formato: "202512" -> 2025)
    const years = new Set<number>();
    this.availableExercicios.forEach(exercicio => {
      if (exercicio && exercicio.length >= 4) {
        const year = parseInt(exercicio.substring(0, 4), 10);
        if (!isNaN(year)) {
          years.add(year);
        }
      }
    });
    this.availableYears = Array.from(years).sort((a, b) => b - a); // Ordena decrescente
  }

  // MODIFICADO: Adicionar lógica para desabilitar meses pares e verificar disponibilidade
  isMonthDisabled(month: number): boolean {
    const selectedDate = new Date(this.selectedYear, month - 1, 1);
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const firstDayOfCurrentMonth = new Date(currentYear, currentMonth, 1);

    // Se desabilitar meses pares estiver ativado, verifica se o mês é par
    if (this.disableEvenMonths && month % 2 === 0) {
      return true;
    }

    // Se há exercícios disponíveis, verifica se o mês/ano existe nos exercícios
    if (this.availableExercicios && this.availableExercicios.length > 0) {
      const exercicioFormat = `${this.selectedYear}${month.toString().padStart(2, '0')}`;
      const isAvailable = this.availableExercicios.includes(exercicioFormat);
      if (!isAvailable) {
        return true;
      }
    }

    // Mantém a lógica original de datas futuras
    return selectedDate > firstDayOfCurrentMonth;
  }

  onYearChange() {
    if (this.selectedMonth && this.isMonthDisabled(this.selectedMonth)) {
      this.selectedMonth = null;
    }
  }

  syncPeriod(year: number, month: number): void {
    this.selectedYear = year;
    this.selectedMonth = month;
    this.originalYear = year;
    this.originalMonth = month;
  }

  openPeriodPicker() {
    if (this.disabled) return;

    if (!this.isPeriodPickerOpen) {
      this.originalYear = this.selectedYear;
      this.originalMonth = this.selectedMonth;
    } else {
      if (this.selectedMonth === null) {
        this.selectedYear = this.originalYear;
        this.selectedMonth = this.originalMonth;
      }
    }

    this.isPeriodPickerOpen = !this.isPeriodPickerOpen;
  }

  selectMonth(month: number) {
    if (this.disabled || this.isMonthDisabled(month)) {
      return;
    }

    this.selectedMonth = month;
    this.isPeriodPickerOpen = false;
    this.originalYear = this.selectedYear;
    this.originalMonth = this.selectedMonth;

    this.periodChange.emit({
      year: this.selectedYear,
      month: this.selectedMonth
    });
  }

  formatPeriodDisplay(): string {
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    if (!this.selectedMonth) {
      return `Selecionar Mês/${this.selectedYear}`;
    }

    return `${monthNames[this.selectedMonth - 1]}/${this.selectedYear}`;
  }

  getShortMonthName(fullMonthName: string): string {
    const monthMap: { [key: string]: string } = {
      'Janeiro': 'Jan', 'Fevereiro': 'Fev', 'Março': 'Mar', 'Abril': 'Abr',
      'Maio': 'Mai', 'Junho': 'Jun', 'Julho': 'Jul', 'Agosto': 'Ago',
      'Setembro': 'Set', 'Outubro': 'Out', 'Novembro': 'Nov', 'Dezembro': 'Dez'
    };
    return monthMap[fullMonthName] || fullMonthName.substring(0, 3);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.period-picker')) {
      if (this.isPeriodPickerOpen && this.selectedMonth === null) {
        this.selectedYear = this.originalYear;
        this.selectedMonth = this.originalMonth;
      }
      this.isPeriodPickerOpen = false;
    }
  }
}
